import React, { Fragment, memo, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import htm from 'htm';
import { formatInspectorValue, extractValidation } from './inspector-utils.js';

const html = htm.bind(React.createElement);

// ============================================================================
// COLLAPSIBLE SECTION
// ============================================================================
export function CollapsibleSection({ title, icon, badge, defaultOpen = true, children, className = '' }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return html`
    <section className="as-collapsible-section ${className} ${isOpen ? 'open' : 'closed'}">
      <button
        type="button"
        className="as-section-header-button"
        onClick=${() => setIsOpen(!isOpen)}
        aria-expanded=${isOpen}
      >
        ${icon && html`<span className="as-section-icon">${icon}</span>`}
        <h4>${title}</h4>
        ${badge && html`<span className="as-section-badge">${badge}</span>`}
        <span className="as-section-expand-icon">›</span>
      </button>
      ${isOpen && html`<div className="as-section-content">${children}</div>`}
    </section>
  `;
}

// ============================================================================
// PRODUCT CARDS
// ============================================================================
export function ProductCards({ products, maxVisible = 5 }) {
  const [showAll, setShowAll] = useState(false);
  const displayProducts = showAll ? products : products.slice(0, maxVisible);
  const hasMore = products.length > maxVisible;

  return html`
    <div className="as-product-cards">
      <div className="as-product-list-compact">
        ${displayProducts.map((product, idx) => html`
          <${ProductCard} key=${idx} product=${product} position=${product.position || idx + 1} />
        `)}
      </div>
      ${hasMore && !showAll && html`
        <button
          type="button"
          className="as-product-show-more"
          onClick=${() => setShowAll(true)}
        >
          +${products.length - maxVisible} more
        </button>
      `}
      ${showAll && hasMore && html`
        <button
          type="button"
          className="as-product-show-more"
          onClick=${() => setShowAll(false)}
        >
          Show less
        </button>
      `}
    </div>
  `;
}

function ProductCard({ product, position }) {
  // Core product details
  const name = product.product || product.name || product.core_product || 'Unnamed Product';
  const sku = product.sku;
  const brand = product.brand;
  const variant = product.variant;
  const size = product.size;
  const entryType = product.entry_type; // Cart Item, Line Item, etc.

  // Pricing details
  const price = product.unit_price ?? product.price;
  const formattedPrice = price !== undefined && price !== null && !isNaN(price)
    ? formatInspectorValue('price', price)
    : null;
  const quantity = product.units ?? product.quantity ?? 1;
  const discountPct = product.discount_percentage;

  // Category
  const category = product.main_category || product.category;

  // Build variant/size string
  const variantInfo = [variant, size].filter(Boolean).join(' / ');

  return html`
    <div className="as-product-card">
      <div className="as-product-info">
        <div className="as-product-name">${name}</div>
        ${entryType && html`<div className="as-product-entry-type">${entryType}</div>`}
        ${sku && html`<div className="as-product-sku">SKU: ${sku}</div>`}
        ${brand && html`<div className="as-product-brand">${brand}</div>`}
        ${variantInfo && html`<div className="as-product-variant">${variantInfo}</div>`}
        ${category && html`<div className="as-product-category">${category}</div>`}
      </div>
      <div className="as-product-pricing">
        ${formattedPrice && html`
          <span className="as-product-price">
            ${formattedPrice}
            ${quantity > 1 ? html`<span className="as-product-qty">×${quantity}</span>` : ''}
          </span>
        `}
        ${discountPct && html`
          <span className="as-product-discount">-${discountPct}%</span>
        `}
      </div>
    </div>
  `;
}

// ============================================================================
// CLASSIFICATION TAGS (GROUPED)
// ============================================================================
export function ClassificationTags({ classifications }) {
  // Group by type and filter out items without values
  const grouped = useMemo(() => {
    const groups = {};
    classifications
      .filter(item => item.value && item.value.trim()) // Only include items with actual values
      .forEach(item => {
        const type = item.type || 'other';
        if (!groups[type]) {
          groups[type] = [];
        }
        groups[type].push(item);
      });
    return groups;
  }, [classifications]);

  const typeLabels = {
    keyword: 'Keywords',
    intent: 'Intents',
    intent_category: 'Intent Categories',
    priority: 'Priority',
    category: 'Category',
    customer_tone: 'Customer Tone',
    technology_and_brands: 'Tech & Brands',
    product: 'Products',
    product_type: 'Product Types',
    resolved: 'Resolved',
    unresolved: 'Unresolved',
    inbox: 'Inbox',
  };

  // If no groups have values, return null
  if (Object.keys(grouped).length === 0) return null;

  return html`
    <div className="as-classification-groups">
      ${Object.entries(grouped).map(([type, items]) => html`
        <div key=${type} className="as-classification-group">
          <div className="as-classification-type-label">
            ${typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1)}
          </div>
          <div className="as-tag-cloud">
            ${items.map((item, idx) => html`
              <span
                key=${idx}
                className="as-tag as-tag-${type}"
                title=${item.reasoning || item.weight ? `${item.reasoning || ''} ${item.weight ? `(weight: ${item.weight})` : ''}`.trim() : null}
              >
                ${item.value || item.label}
                ${item.confidence && html`<span className="as-tag-confidence">${Math.round(item.confidence * 100)}%</span>`}
                ${item.score !== undefined && item.score !== null && html`<span className="as-tag-score">${Math.round(item.score * 100)}%</span>`}
              </span>
            `)}
          </div>
        </div>
      `)}
    </div>
  `;
}

// ============================================================================
// ANALYSIS / COST SUMMARY
// ============================================================================
export function AnalysisSummary({ analyses }) {
  const totalCost = useMemo(() =>
    analyses.reduce((sum, a) => sum + (a.amount || 0), 0),
    [analyses]
  );

  return html`
    <div className="as-analysis-summary">
      ${analyses.slice(0, 5).map((analysis, idx) => html`
        <div key=${idx} className="as-analysis-item">
          <span className="as-analysis-provider">${analysis.provider || 'Unknown'}${analysis.variant ? ` (${analysis.variant})` : ''}</span>
          <span className="as-analysis-cost">$${(analysis.amount || 0).toFixed(4)}</span>
        </div>
      `)}
      ${totalCost > 0 && html`
        <div className="as-analysis-total">
          <span>Total Cost (${analyses.length} ${analyses.length === 1 ? 'call' : 'calls'})</span>
          <span>$${totalCost.toFixed(4)}</span>
        </div>
      `}
    </div>
  `;
}

// ============================================================================
// CHAT TRANSCRIPT
// ============================================================================
export function ChatTranscript({ content }) {
  const [showFormatted, setShowFormatted] = useState(true);
  const transcriptText = content.transcript || '';
  const formattedHtml = content.formatted_transcript || '';

  // Parse plain transcript into messages
  const messages = useMemo(() => {
    if (!transcriptText) return [];

    const parts = transcriptText.split('---\n');
    return parts.map(part => {
      const lines = part.trim().split('\n');
      const msg = {};
      lines.forEach(line => {
        if (line.startsWith('Sender: ')) msg.sender = line.substring(8);
        else if (line.startsWith('Text: ')) msg.text = line.substring(6);
        else if (line.startsWith('Tool call: ')) msg.tool = line.substring(11);
        else if (line.startsWith('Timestamp: ')) msg.timestamp = line.substring(11);
      });
      return msg;
    }).filter(msg => msg.sender);
  }, [transcriptText]);

  return html`
    <div className="as-chat-transcript">
      <div className="as-chat-controls">
        <button
          type="button"
          className="as-chat-toggle ${showFormatted ? 'active' : ''}"
          onClick=${() => setShowFormatted(true)}
        >
          Formatted
        </button>
        <button
          type="button"
          className="as-chat-toggle ${!showFormatted ? 'active' : ''}"
          onClick=${() => setShowFormatted(false)}
        >
          Plain
        </button>
      </div>

      ${showFormatted && formattedHtml ? html`
        <div
          className="as-chat-formatted"
          dangerouslySetInnerHTML=${{ __html: formattedHtml }}
        />
      ` : html`
        <div className="as-chat-messages">
          ${messages.map((msg, idx) => html`
            <div key=${idx} className="as-chat-message as-chat-${msg.sender}">
              <div className="as-chat-sender">
                ${msg.sender === 'user' ? 'User' : 'Assistant'}
              </div>
              ${msg.text && html`<div className="as-chat-text">${msg.text}</div>`}
              ${msg.tool && html`<div className="as-chat-tool">Tool: ${msg.tool}</div>`}
              ${msg.timestamp && html`<div className="as-chat-timestamp">${msg.timestamp}</div>`}
            </div>
          `)}
        </div>
      `}
    </div>
  `;
}

// ============================================================================
// COPYABLE VALUE
// ============================================================================
export function CopyableValue({ value, label }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Truncate long values
  const displayValue = value && value.length > 45
    ? value.substring(0, 42) + '...'
    : value;

  return html`
    <div className="as-copyable-value" title=${value}>
      <span className="as-copyable-text">${displayValue}</span>
      <button
        type="button"
        className="as-copy-btn ${copied ? 'copied' : ''}"
        onClick=${handleCopy}
        aria-label=${`Copy ${label || 'value'}`}
      >
        ${copied ? '✓' : '⎘'}
      </button>
    </div>
  `;
}

// ============================================================================
// LOCATION CARD
// ============================================================================
export function LocationCard({ location }) {
  const {
    country, country_code, region, municipality, locality, postal_code, postal_name,
    latitude, longitude, location_of, label, street, street_nr, address
  } = location;

  const hasCoords = latitude !== undefined && latitude !== null &&
                    longitude !== undefined && longitude !== null;
  const mapsUrl = hasCoords ? `https://www.google.com/maps?q=${latitude},${longitude}` : null;

  // Build location items for definition grid
  const items = [];

  if (location_of) items.push({ label: 'Type', value: location_of });
  if (label) items.push({ label: 'Label', value: label });
  if (country) items.push({ label: 'Country', value: country_code ? `${country} (${country_code})` : country });

  // City
  const city = locality || municipality;
  if (city) items.push({ label: 'City', value: city });

  // Region/State
  if (region) items.push({ label: 'Region', value: region });

  // Address
  const fullAddress = [street_nr, street].filter(Boolean).join(' ').trim() || address;
  if (fullAddress) items.push({ label: 'Address', value: fullAddress });

  // Postal
  const postalDisplay = postal_code && postal_name && postal_code !== postal_name
    ? `${postal_code} (${postal_name})`
    : (postal_code || postal_name);
  if (postalDisplay) items.push({ label: 'Postal Code', value: postalDisplay });

  // Coordinates with map link
  if (hasCoords) {
    const coordsValue = html`
      <span className="as-location-coords-value">
        ${latitude.toFixed(4)}, ${longitude.toFixed(4)}
        ${mapsUrl && html`
          <a
            href=${mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="as-maps-link"
            title="Open in Google Maps"
          >
            Map
          </a>
        `}
      </span>
    `;
    items.push({ label: 'Coordinates', value: coordsValue });
  }

  return html`<${DefinitionGrid} items=${items} />`;
}

// ============================================================================
// ENTITY PARTICIPANTS
// ============================================================================
export function ParticipantsList({ participants }) {
  return html`
    <dl className="as-definition-grid">
      ${participants.map((p, idx) => {
        // Determine the label - prioritize role, then entity_type, then id_type
        const label = p.role || p.entity_type || p.id_type || 'Participant';
        // Determine the value - use label if exists, otherwise id
        const value = p.label || p.id || 'Unknown';

        return html`
          <${Fragment} key=${idx}>
            <dt>${label}</dt>
            <dd>
              ${p.id ? html`<${CopyableValue} value=${value} label=${label} />` : value}
            </dd>
          <//>
        `;
      })}
    </dl>
  `;
}

// ============================================================================
// NORMALIZATION TABLE (for Developer Info)
// ============================================================================
export function NormalizationTable({ changes, grouped }) {
  // Group changes by action type for better organization
  const actionOrder = ['CORRECTED', 'RENAMED', 'MOVED', 'MODIFIED', 'ADDED', 'REMOVED'];
  const sortedGroups = Object.keys(grouped).sort((a, b) => {
    const aIdx = actionOrder.indexOf(a);
    const bIdx = actionOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  return html`
    <div className="as-normalization-table-wrapper">
      ${sortedGroups.map(action => html`
        <div key=${action} className="as-normalization-group">
          <div className="as-normalization-action-header">
            ${action} (${grouped[action].length})
          </div>
          <table className="as-normalization-table">
            <thead>
              <tr>
                <th>Original Path</th>
                <th></th>
                <th>Normalized Path</th>
              </tr>
            </thead>
            <tbody>
              ${grouped[action].map((change, idx) => html`
                <tr key=${idx}>
                  <td className="as-norm-from">${change.from || '—'}</td>
                  <td className="as-norm-arrow">→</td>
                  <td className="as-norm-to">${change.to || '—'}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      `)}
    </div>
  `;
}

// ============================================================================
// DEFINITION GRID (2-column for better space usage)
// ============================================================================
export function DefinitionGrid({ items }) {
  if (!items || items.length === 0) return null;

  return html`
    <dl className="as-definition-grid">
      ${items.map(({ label, value }, idx) => {
        // Check if value is already a CopyableValue component or other React element
        const isReactElement = value && typeof value === 'object' && value.type;

        // Handle null/undefined values
        const displayValue = value === null || value === undefined || value === 'null' ? '—' : value;

        return html`
          <${Fragment} key=${idx}>
            <dt>${label}</dt>
            <dd>
              ${isReactElement ? displayValue : html`<span title=${typeof displayValue === 'string' && displayValue.length > 50 ? displayValue : null}>${displayValue}</span>`}
            </dd>
          <//>
        `;
      })}
    </dl>
  `;
}
